import { useState } from 'react';
import type { TreeNode } from '../../lib/runAssets';

interface Props {
  root: TreeNode;
  onSelect: (path: string) => void;
  selected?: string | null;
}

interface NodeRowProps {
  node: TreeNode;
  depth: number;
  onSelect: (path: string) => void;
  selected?: string | null;
  defaultOpen?: boolean;
}

function NodeRow({ node, depth, onSelect, selected, defaultOpen }: NodeRowProps) {
  const [open, setOpen] = useState(!!defaultOpen);
  const isSelected = node.path === selected;
  if (node.type === 'directory') {
    return (
      <div className="tree-node">
        <div
          className={`tree-row tree-dir ${isSelected ? 'selected' : ''}`}
          style={{ paddingLeft: 6 + depth * 14 }}
          onClick={() => setOpen(!open)}
        >
          <span className="tree-twisty">{open ? '\u25BE' : '\u25B8'}</span>
          <span className="tree-icon">{'\u{1F4C1}'}</span>
          <span className="tree-name">{node.name || '(root)'}</span>
        </div>
        {open && node.children?.map((child) => (
          <NodeRow
            key={child.path}
            node={child}
            depth={depth + 1}
            onSelect={onSelect}
            selected={selected}
          />
        ))}
      </div>
    );
  }
  return (
    <div
      className={`tree-row tree-file ${isSelected ? 'selected' : ''}`}
      style={{ paddingLeft: 6 + depth * 14 + 16 }}
      onClick={() => onSelect(node.path)}
      title={node.path}
    >
      <span className="tree-icon">{'\u{1F4C4}'}</span>
      <span className="tree-name">{node.name}</span>
      {node.size !== undefined && (
        <span className="muted small tree-size">{formatSize(node.size)}</span>
      )}
    </div>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

/**
 * Collapsible file tree. Renders the entire walked tree client-side; we cap
 * the server-side walk depth in `treeWalk.ts` to keep payloads reasonable.
 */
export function SourceTree({ root, onSelect, selected }: Props) {
  return (
    <div className="source-tree">
      <NodeRow node={root} depth={0} onSelect={onSelect} selected={selected} defaultOpen />
    </div>
  );
}
